// Simple Audio File Service - Only uses audio files
class AudioFileService {
  private sounds: Map<string, HTMLAudioElement> = new Map();
  private enabled = true;
  private volume: number = 0.3;
  private mobileUnlocked: boolean = false;
  private isMobileDevice: boolean = false;
  private soundList = [
    { name: 'click', file: 'sounds/ui/click.wav' },
    { name: 'hover', file: 'sounds/ui/hover.ogg' },
    { name: 'hint', file: 'sounds/ui/hint.wav' },
    { name: 'tick', file: 'sounds/ui/tick.wav' },
    { name: 'correct', file: 'sounds/game/correct.wav' },
    { name: 'incorrect', file: 'sounds/game/incorrect.wav' },
    { name: 'perfect', file: 'sounds/game/perfect.mp3' },
    { name: 'gameStart', file: 'sounds/game/game-start.wav' },
    { name: 'gameEnd', file: 'sounds/game/game-end.wav' },
    { name: 'achievement', file: 'sounds/achievements/achievement.wav' },
    { name: 'levelUp', file: 'sounds/achievements/level-up.mp3' }
  ];

  constructor() {
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const savedAudioState = localStorage.getItem('audioEnabled');
    this.enabled = savedAudioState !== 'false';
    
    // Load sounds immediately on desktop, after user interaction on mobile
    if (!this.isMobileDevice) {
      this.loadSounds();
      this.mobileUnlocked = true; // Desktop doesn't need unlock
    } else {
      this.setupMobileUnlock();
    }
  }

  private setupMobileUnlock(): void {
    const unlock = async () => {
      if (this.mobileUnlocked) return;
      
      // Load sounds if not loaded
      if (this.sounds.size === 0) {
        this.loadSounds();
        // Wait for at least one sound to load
        await this.waitForSounds();
      }
      
      // Try to unlock with just one sound
      const firstSound = this.sounds.values().next().value;
      if (firstSound) {
        try {
          firstSound.volume = 0;
          await firstSound.play();
          firstSound.pause();
          firstSound.currentTime = 0;
          firstSound.volume = this.volume;
          this.mobileUnlocked = true;
        } catch (error) {
          // Silent failure
        }
      }
    };

    document.addEventListener('touchstart', unlock, { once: true });
    document.addEventListener('click', unlock, { once: true });
  }

  private async waitForSounds(): Promise<void> {
    let attempts = 0;
    while (this.sounds.size === 0 && attempts < 30) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem('audioEnabled', enabled.toString());
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  async play(soundName: string): Promise<void> {
    if (!this.enabled) return;

    const audio = this.sounds.get(soundName);
    if (!audio) return;

    try {
      // Always clone to allow multiple simultaneous playback
      const clone = audio.cloneNode() as HTMLAudioElement;
      clone.volume = this.volume;
      await clone.play();
    } catch (error) {
      // Silent failure
    }
  }

  async unlockMobileAudio(): Promise<void> {
    if (!this.isMobileDevice || this.mobileUnlocked) {
      return;
    }
    
    // Load sounds if needed
    if (this.sounds.size === 0) {
      this.loadSounds();
      await this.waitForSounds();
    }
    
    // Test with just the click sound
    const clickSound = this.sounds.get('click');
    if (clickSound) {
      try {
        clickSound.volume = 0;
        await clickSound.play();
        clickSound.pause();
        clickSound.currentTime = 0;
        clickSound.volume = this.volume;
        this.mobileUnlocked = true;
      } catch (error) {
        // Silent failure
      }
    }
  }

  private loadSounds(): void {
    this.soundList.forEach(({ name, file }) => {
      const audio = new Audio(file);
      audio.volume = this.volume;
      
      // Optimize for platform
      if (this.isMobileDevice) {
        audio.preload = 'none';
        audio.setAttribute('playsinline', '');
        audio.setAttribute('webkit-playsinline', '');
      } else {
        audio.preload = 'auto';
      }
      
      // Single event listener approach
      const onLoad = () => {
        this.sounds.set(name, audio);
      };
      
      const onError = () => {
        // Silent failure
      };
      
      audio.addEventListener('canplaythrough', onLoad, { once: true });
      audio.addEventListener('loadeddata', onLoad, { once: true });
      audio.addEventListener('error', onError, { once: true });
      
      // Trigger loading
      if (this.isMobileDevice) {
        audio.load();
      }
    });
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    this.sounds.forEach(audio => {
      audio.volume = this.volume;
    });
  }

  getVolume(): number {
    return this.volume;
  }

  getSoundsStatus(): { [key: string]: boolean } {
    const status: { [key: string]: boolean } = {};
    this.soundList.forEach(({ name }) => {
      status[name] = this.sounds.has(name);
    });
    return status;
  }

  getLoadedCount(): { loaded: number; total: number } {
    return {
      loaded: this.sounds.size,
      total: this.soundList.length
    };
  }

  async prepareAudio(): Promise<void> {
    await this.unlockMobileAudio();
  }
}

// Export singleton instance
const audioFileService = new AudioFileService();
export default audioFileService; 